import type { UIMessage } from 'ai';

/**
 * The live progress of a running delegation (src/tools/delegation-progress.ts):
 * a `data-delegation-progress` part keyed by the delegation's own tool call id.
 * Each frame is a whole snapshot and the AI SDK replaces the part by id, so a
 * re-sent or replayed frame never adds steps. Dependency-free, like
 * ordered-segments.ts, so the phone surface could share it.
 */

export type DelegationStepStatus = 'running' | 'done' | 'error';

export interface DelegationStepView {
  id: string;
  label: string;
  status: DelegationStepStatus;
}

export interface DelegationProgressView {
  steps: DelegationStepView[];
  text: string;
}

const STATUSES: ReadonlySet<string> = new Set(['running', 'done', 'error']);

function readStep(raw: unknown): DelegationStepView | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (typeof s.id !== 'string' || typeof s.label !== 'string' || typeof s.status !== 'string') return null;
  if (!STATUSES.has(s.status)) return null;
  return { id: s.id, label: s.label, status: s.status as DelegationStepStatus };
}

/** The progress for one delegate call, or null when there is nothing to show. */
export function readDelegationProgress(parts: UIMessage['parts'], toolCallId: string): DelegationProgressView | null {
  const part = parts.find(
    (p) => p.type === 'data-delegation-progress' && (p as { id?: unknown }).id === toolCallId,
  );
  return readDelegationProgressData((part as { data?: unknown } | undefined)?.data);
}

/**
 * One progress snapshot's data, or null when there is nothing to show. The
 * same frame also reaches a chat task's panel over the session channel
 * (FRONT-3211), where it is not a message part.
 */
export function readDelegationProgressData(data: unknown): DelegationProgressView | null {
  if (!data || typeof data !== 'object') return null;
  const raw = data as { steps?: unknown; text?: unknown };
  // Keyed by id: a repeated id replaces the earlier step, never adds a second row.
  const steps = new Map<string, DelegationStepView>();
  for (const s of Array.isArray(raw.steps) ? raw.steps : []) {
    const step = readStep(s);
    if (step) steps.set(step.id, step);
  }
  const text = typeof raw.text === 'string' ? raw.text : '';
  if (steps.size === 0 && !text.trim()) return null;
  return { steps: [...steps.values()], text };
}
